import { computed, reactive, markRaw } from "vue";
import {
  eachDayOfInterval,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from "date-fns";

import { CalendarEvent } from "frontend/interfaces/calendar";
import { Note } from "frontend/interfaces/note";
import { ScheduleActivation } from "frontend/interfaces/schedule-activation";
import { Holiday } from "frontend/interfaces/settings/holiday";
import { newNote } from "frontend/parser/parse-note";
import { Database } from "frontend/uses/abstract-view/use-abstract-view-parser";
import { NoteModal } from "frontend/utils/modals/note-modal";
import {
  ConditionalContextMenuEntry,
  ContextMenuAPI,
} from "shared/utils/context-menu/interfaces";
import {
  ContextMenuCategory,
  ContextMenuPositions,
} from "shared/utils/context-menu/positions";
import { deleteNote } from "frontend/api/application/request-note";
import { useSimpleModal } from "frontend/uses/simple-modal/use-simple-modal";
import { PrimaryKey } from "frontend/interfaces/primary-key";

import SimpleEntry from "shared/utils/context-menu/components/SimpleEntry.vue";

export function holiday(
  holiday: Holiday,
  personId: PrimaryKey,
  columns: number[]
): CalendarEvent {
  return {
    key: `${holiday.id}-person--${personId}`,
    label: holiday.name,
    color: "#cc1512",
    columns,
    row: 0,
    startsBefore: false,
    endsAfter: false,
  };
}

export function note(
  database: Database,
  note: Note,
  personId: PrimaryKey,
  columns: number[],
  dates?: Date[]
): CalendarEvent {
  const newStart = dates
    ? dates.find((d) => d >= startOfDay(note.from) && d <= endOfDay(note.to))
    : undefined;

  const newEnd = dates
    ? [...dates]
        .reverse()
        .find((d) => d >= startOfDay(note.from) && d <= endOfDay(note.to))
    : undefined;

  return {
    key: `${note.id}-person--${personId}`,
    label: note.name,
    color: note.color,
    columns,
    row: 0,
    startsBefore: !!newStart && isBefore(note.from, startOfDay(newStart)),
    endsAfter: !!newEnd && isAfter(note.to, endOfDay(newEnd)),
    ctxEditEvent: ctxEditNote(note, database),
    ctxDeleteEvent: ctxDeleteNote(note, database),
  };
}

export function scheduleActivation(
  activation: ScheduleActivation,
  columns: number[],
  dates: Date[]
): CalendarEvent {
  return {
    key: activation.id,
    label: activation.schedule_name,
    color: "#147f14",
    columns,
    row: 0,
    startsBefore: activation.from ? isBefore(activation.from, dates[0]) : true,
    endsAfter: activation.to
      ? isAfter(activation.to, dates[dates.length - 1])
      : true,
  };
}

export function onClickCreateNote(
  database: Database,
  personId: string | null,
  date: Date
) {
  return async () => {
    const data = reactive({
      note: newNote(personId, date),
      editMode: false,
      errorDatabase: {},
      isLoading: false,
    });

    await new NoteModal()
      .setData(data)
      .onSuccess(() => database.connection?.triggerRefreshNeeded())
      .show();
  };
}

export function isActivationOnDate(
  from: Date,
  to: Date,
  date: Date,
  dates: Date[]
): boolean {
  const newFrom = dates.find((d) => d >= startOfDay(from) && d <= endOfDay(to));
  return !!newFrom && isSameDay(newFrom, date);
}

export function isNotePartOfPerson(note: Note, personId: string) {
  return note.client_resources.persons.includes(personId);
}

export function isNotePartOfDates(note: Note, dates: Date[]) {
  return dates.some((d) => d >= note.from && d <= note.to);
}

export function getEventRows(events: CalendarEvent[]): CalendarEvent[] {
  const rows: CalendarEvent[][] = [];

  const overlaps = (a: number[], b: number[]) => a.some((x) => b.includes(x));

  events.forEach((event) => {
    const index = rows.findIndex((row) =>
      row.every((existing) => !overlaps(existing.columns, event.columns))
    );

    if (index !== -1) {
      rows[index].push(event);
      event.row = index;
    } else {
      event.row = rows.length;
      rows.push([event]);
    }
  });

  return events;
}

export function getEventColumns(from: Date, to: Date, dates: Date[]) {
  const days = eachDayOfInterval({
    start: from,
    end: to,
  });
  const columns: number[] = [];

  days.forEach((day) => {
    const index = dates.findIndex((d) => isSameDay(d, day));
    if (index !== -1) {
      columns.push(index);
    }
  });

  return columns;
}

function ctxEditNote(
  note: Note,
  database: Database
): ConditionalContextMenuEntry {
  return {
    condition: computed(() => !!note),
    entry: {
      target: computed(() => null),
      component: markRaw(SimpleEntry),
      position: ContextMenuPositions.editNote,
      category: ContextMenuCategory.noteActions,
      onTrigger: async (api: ContextMenuAPI) => {
        new NoteModal()
          .setData(
            reactive({
              note,
              editMode: true,
              errorDatabase: {},
              isLoading: false,
            })
          )
          .onSuccess(() => database.connection?.triggerRefreshNeeded())
          .show();
        api.doClose();
      },
      options: reactive({
        iconName: computed(() => null),
        label: "Notiz Bearbeiten",
      }),
    },
  };
}

function ctxDeleteNote(
  note: Note,
  database: Database
): ConditionalContextMenuEntry {
  return {
    condition: computed(() => !!note),
    entry: {
      target: computed(() => null),
      component: markRaw(SimpleEntry),
      position: ContextMenuPositions.deleteNote,
      category: ContextMenuCategory.noteActions,
      onTrigger: async (api: ContextMenuAPI) => {
        const { confirm } = useSimpleModal();

        const result = await confirm(
          "Sind Sie sicher, dass Sie die Notiz löschen möchten?",
          { title: "Bestätigung" }
        );

        if (result.confirm) {
          const result = await deleteNote(note.id);
          const { success } = result;

          if (success) {
            database.connection?.triggerRefreshNeeded();
          }
        }
        api.doClose();
      },
      options: reactive({
        iconName: computed(() => null),
        label: "Notiz Löschen",
      }),
    },
  };
}
